import { bindable, children, customElement, BindingMode, INode } from 'aurelia';

import { watch } from '@aurelia/runtime-html';

@customElement({
    name: 'wizard',
    template: '<au-slot></au-slot>',
})
export class Wizard {
    @bindable({ mode: BindingMode.toView }) options;
    @bindable onRender;
    @bindable onNavigate;

    currentStep;
    currentStepIndex = -1;

    @children({ filter: (e: HTMLElement) => e.tagName === 'STEP' }) wizardSteps;

    constructor(@INode readonly element) {}

    @watch('currentStep')
    currentStepChanged() {
        if (this.onRender) {
            this.onRender({
                currentStepIndex: this.currentStepIndex,
                currentStep: this.currentStep,
            });
        }
    }

    @watch('wizardSteps.length')
    wizardStepsChanged() {
        if (this.wizardSteps && this.wizardSteps.length > 0) {
            this.currentStepIndex = 0;

            this.currentStep = this.wizardSteps[0];

            if (typeof this.currentStep.stepEnter === 'function') {
                this.currentStep.stepEnter('forward');
            }

            this.currentStep.completed = false;
            this.currentStep.selected = true;
        }
    }

    hasStep(stepIndex) {
        return (
            this.wizardSteps &&
            this.wizardSteps.length > 0 &&
            0 <= stepIndex &&
            stepIndex < this.wizardSteps.length
        );
    }

    hasPreviousStep() {
        return this.hasStep(this.currentStepIndex - 1);
    }

    hasNextStep() {
        return this.hasStep(this.currentStepIndex + 1);
    }

    get isFirstStep() {
        if (!this.wizardSteps) {
            return false;
        }

        return this.wizardSteps.length > 0 && this.currentStepIndex === 0;
    }

    get isLastStep() {
        if (!this.wizardSteps) {
            return false;
        }

        return (
            this.wizardSteps.length > 0 &&
            this.currentStepIndex === this.wizardSteps.length - 1
        );
    }

    get canGoToPreviousStep() {
        const previousStepIndex = this.currentStepIndex - 1;

        return (
            this.hasStep(previousStepIndex) &&
            this.canGoToStep(previousStepIndex)
        );
    }

    get canGoToNextStep() {
        const nextStepIndex = this.currentStepIndex + 1;

        return this.hasStep(nextStepIndex) && this.canGoToStep(nextStepIndex);
    }

    get currentStepIsValid() {
        return this.currentStep.canExitStep();
    }

    preventCurrentStepNavigation() {
        this.currentStep.stepCanExitComputed = false;
    }

    enableCurrentStepNavigation() {
        this.currentStep.stepCanExitComputed = true;
    }

    getStepAtIndex(stepIndex) {
        if (!this.hasStep(stepIndex)) {
            throw new Error(`Could not find step at this stepIndex`);
        }

        return this.wizardSteps.find(
            (item, index, array) => index === stepIndex
        );
    }

    getMovingDirection(destinationStep) {
        let movingDirection;

        if (destinationStep > this.currentStepIndex) {
            movingDirection = 'forward';
        } else if (destinationStep < this.currentStepIndex) {
            movingDirection = 'backward';
        } else {
            movingDirection = 'stay';
        }

        return movingDirection;
    }

    goToPreviousStep() {
        if (this.hasPreviousStep()) {
            this.goToStep(this.currentStepIndex - 1);
        }
    }

    goToNextStep() {
        if (this.hasNextStep()) {
            this.goToStep(this.currentStepIndex + 1);
        }
    }

    canGoToStep(stepIndex) {
        let result = this.hasStep(stepIndex);

        this.wizardSteps.forEach((wizardStep, index) => {
            if (index < stepIndex && index !== this.currentStepIndex) {
                result =
                    result && (wizardStep.completed || wizardStep.optional);
            }
        });

        return result;
    }

    goToStep(destinationStepIndex) {
        const destinationStep = this.getStepAtIndex(destinationStepIndex);

        const movingDirection = this.getMovingDirection(destinationStepIndex);

        this.currentStep
            .canExitStep(movingDirection)
            .then(() => {
                this.wizardSteps.forEach((wizardStep, index) => {
                    if (index === this.currentStepIndex) {
                        wizardStep.completed = true;
                    }

                    if (
                        this.currentStepIndex > destinationStepIndex &&
                        index > destinationStepIndex
                    ) {
                        wizardStep.completed = false;
                    }
                });

                if (typeof this.currentStep.stepExit === 'function') {
                    this.currentStep.stepExit({ direction: movingDirection });
                }

                this.currentStep.selected = false;

                this.currentStepIndex = destinationStepIndex;
                this.currentStep = destinationStep;

                if (typeof this.currentStep.stepEnter === 'function') {
                    this.currentStep.stepEnter({ direction: movingDirection });
                }

                this.currentStep.selected = true;

                if (this.onNavigate) {
                    this.onNavigate({
                        destinationStep: destinationStep,
                        movingDirection: movingDirection,
                        currentStepIndex: this.currentStepIndex,
                        currentStep: this.currentStep,
                    });
                }
            })
            .catch((e) => {
                if (typeof this.currentStep.stepExit === 'function') {
                    this.currentStep.stepExit({ direction: 'stay' });
                }

                if (typeof this.currentStep.stepEnter === 'function') {
                    this.currentStep.stepEnter({ direction: 'stay' });
                }
            });
    }
}

    import { Metadata as $$M } from '@aurelia/metadata';
    import { ExpressionKind as $$EK } from '@aurelia/runtime';
    import { Controller as $$C, CustomElement as $$CE, IHydrationContext as $$IHC } from '@aurelia/runtime-html';

    // @ts-ignore
    const controllers = [];

    // @ts-ignore
    if (module.hot) {

    // @ts-ignore
    module.hot.accept();

    // @ts-ignore
    const hot = module.hot;

    let aurelia = hot.data?.aurelia;

    // @ts-ignore
    document.addEventListener('au-started', (event) => {aurelia= event.detail; });
    const currentClassType = Wizard;

    // @ts-ignore
    const proto = Wizard.prototype

    // @ts-ignore
    const ogCreated = proto ? proto.created : undefined;

    if (proto) {
      // @ts-ignore
      proto.created = function(controller) {
        // @ts-ignore
        ogCreated && ogCreated.call(this, controller);
        controllers.push(controller);
      }
    }

    // @ts-ignore
    hot.dispose(function (data) {
      // @ts-ignore
      data.controllers = controllers;
      data.aurelia = aurelia;
    });

    if (hot.data?.aurelia) {
      const newDefinition = $$CE.getDefinition(currentClassType);
      $$M.define(newDefinition.name, newDefinition, currentClassType);
      $$M.define(newDefinition.name, newDefinition, newDefinition);
      hot.data.aurelia.container.res[$$CE.keyFrom(newDefinition.name)] = newDefinition;

      const previousControllers = hot.data.controllers ?? [];
      if(previousControllers.length === 0) {
        // @ts-ignore
        hot.invalidate?.();
      }

      // @ts-ignore
      previousControllers.forEach(controller => {
        const values = { ...controller.viewModel };
        const hydrationContext = controller.container.get($$IHC)
        const hydrationInst = hydrationContext.instruction;

        const bindableNames = Object.keys(controller.definition.bindables);
        // @ts-ignore
        Object.keys(values).forEach(key => {
          if (bindableNames.includes(key)) {
            return;
          }
          // if there' some bindings that target the existing property
          // @ts-ignore
          const isTargettedByBinding = controller.bindings?.some(y =>
            y.ast?.$kind === $$EK.AccessScope
              && y.ast.name === key && y.targetProperty
          );
          if (!isTargettedByBinding) {
            delete values[key];
          }
        });
        const h = controller.host;
        delete controller._compiledDef;
        controller.viewModel = controller.container.invoke(currentClassType);
        controller.definition = newDefinition;
        Object.assign(controller.viewModel, values);
        if (controller._hydrateCustomElement) {
          controller._hydrateCustomElement(hydrationInst, hydrationContext);
        } else {
          controller.hE(hydrationInst, hydrationContext);
        }
        h.parentNode.replaceChild(controller.host, h);
        controller.hostController = null;
        controller.deactivate(controller, controller.parent ?? null, 0);
        controller.activate(controller, controller.parent ?? null, 0);
      });
    }
  }